import { Lock, Key, Wrench, ShieldCheck, DoorClosed, AlertCircle } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "react-router-dom";
import ouverturePorteSrc from "@/assets/services/ouverture-porte.jpg";
import changementSerrureSrc from "@/assets/services/changement-serrure.jpg";
import reparationSerrureSrc from "@/assets/services/reparation-serrure.jpg";
import voletRoulantSrc from "@/assets/services/volet-roulant.jpg";
import porteBlindeeSrc from "@/assets/services/porte-blindee.jpg";
import depannageUrgenceSrc from "@/assets/heroes/depannage-urgence-hero.jpg";

interface ServicesProps {
  location?: string;
}

const services = [
  {
    icon: AlertCircle,
    image: depannageUrgenceSrc,
    alt: "Dépannage serrurerie urgence 24h/24 à Villeurbanne et Lyon",
    title: "Dépannage d'urgence",
    description: "Intervention express 24h/24 et 7j/7 pour tous vos besoins urgents en serrurerie.",
    link: "/depannage-urgence"
  },
  {
    icon: DoorClosed,
    image: ouverturePorteSrc,
    alt: "Serrurier professionnel ouvrant une porte à Villeurbanne avec outils spécialisés",
    title: "Ouverture de porte",
    description: "Ouverture de porte claquée ou fermée à clé. Intervention sans casse dans la mesure du possible.",
    link: "/ouverture-porte"
  },
  {
    icon: Key,
    image: changementSerrureSrc,
    alt: "Changement de cylindre de serrure par serrurier Lyon",
    title: "Changement de serrure",
    description: "Remplacement de tous types de serrures : cylindre, serrure multipoints, serrure encastrée.",
    link: "/changement-serrure"
  },
  {
    icon: Wrench,
    image: reparationSerrureSrc,
    alt: "Réparation de serrure endommagée par serrurier professionnel Villeurbanne",
    title: "Réparation de serrure",
    description: "Réparation de serrure endommagée, cylindre grippé ou cassé. Remise en état rapide.",
    link: "/reparation-serrure"
  },
  {
    icon: Lock,
    image: voletRoulantSrc,
    alt: "Installation et réparation de volet roulant Lyon et Villeurbanne",
    title: "Volet roulant",
    description: "Dépannage, installation et réparation de volets roulants manuels et électriques.",
    link: "/volet-roulant"
  },
  {
    icon: ShieldCheck,
    image: porteBlindeeSrc,
    alt: "Installation de porte blindée sécurisée à Villeurbanne",
    title: "Porte blindée & garage",
    description: "Installation de portes blindées et portes de garage. Sécurisation optimale de votre domicile.",
    link: "/porte-blindee-garage"
  }
];

const Services = ({ location = "Villeurbanne et Grand Lyon" }: ServicesProps) => {
  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Nos services de serrurerie {location && `à ${location}`}</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Intervention rapide et professionnelle pour tous vos besoins en serrurerie
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Link key={index} to={service.link}>
                <Card className="hover:shadow-lg transition-all duration-300 h-full overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.alt}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 h-12 w-12 bg-background rounded-lg flex items-center justify-center shadow-lg">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
